export function buildTabConfiguration(columnWidths, metricsItems) {
  const config = {};
  if (columnWidths && Object.keys(columnWidths).length > 0) {
    config.widths = columnWidths;
  }
  if (metricsItems && metricsItems.length > 0) {
    config.metrics = metricsItems;
  }
  return config;
}

export function buildConfigurationPayload(savedReport, tabStates) {
  const result = {};
  if (!savedReport) return result;

  const addTab = (internalName, entry) => {
    if (!internalName || !entry) return;
    const tabConfig = buildTabConfiguration(
      entry.columnWidths,
      entry.metricsItems
    );
    if (Object.keys(tabConfig).length > 0) {
      result[internalName] = tabConfig;
    }
  };

  if (savedReport.reports) {
    let outputIdx = 0;
    (savedReport.reports ?? []).forEach((subReport) => {
      if (subReport.notForOutput) return;
      addTab(subReport.internalName, tabStates.get(outputIdx++));
    });
  } else {
    addTab(savedReport.internalName, tabStates.get(0));
  }

  return result;
}

/**
 * Moves a one-tab configuration from the tab keyed by fromKey (or the only
 * tab, when fromKey is unknown) to toKey. A report's output tab is keyed by
 * the report's own id, which a copy does not keep.
 */
export function moveTabConfiguration(configuration, fromKey, toKey) {
  if (!configuration) return configuration;
  const entries = Object.values(configuration);
  const tabConfig =
    configuration[fromKey] ?? (entries.length === 1 ? entries[0] : null);
  return tabConfig ? { [toKey]: tabConfig } : {};
}

/**
 * Returns the configuration to store for a copy of a report. A single-table
 * report's tab follows the copy's id; a multi-table report's tabs are keyed
 * by sub-reports, which keep their names, and are returned as is.
 */
export function buildConfigurationForCopy(report, configuration, fromId, toId) {
  if (report?.reports) return configuration;
  return moveTabConfiguration(configuration, fromId, toId);
}

/**
 * Copies configuration-held state (column widths, and metrics when the new
 * entry has none) from previous tab entries onto freshly rebuilt ones.
 */
export function carryOverConfigurationState(prevTabStates, nextTabStates) {
  const result = new Map(nextTabStates);
  result.forEach((entry, idx) => {
    const prev = prevTabStates?.get(idx);
    if (!prev) return;
    const keepMetrics = !(entry.metricsItems?.length > 0);
    result.set(idx, {
      ...entry,
      columnWidths: prev.columnWidths,
      savedColumnWidths: prev.savedColumnWidths,
      ...(keepMetrics && {
        metricsItems: prev.metricsItems,
        savedMetricsItems: prev.savedMetricsItems,
      }),
    });
  });
  return result;
}
